import PropTypes from 'prop-types';
import { getDevice } from 'framework7';
import React, { useEffect, useState } from 'react';
import { Link, Page, Tab, Tabs, Toolbar as ToolbarF7 } from 'framework7-react';

import { I18n } from 'Locales';
import { mockFunction } from 'Helpers';
import { NavigationService } from 'Services';
import { CampaignHand, PlayArrow } from 'Icons';
import { ColumnView, RowView } from 'Containers';
import { AppHeader, Banner, Button, Fab } from 'Components';
import { ALERT_TYPES, COUNTRIES_ADDRESS, EMPTY_TABS, ICON_COLOR, PAGE_NAMES } from 'Constants';

import './style.scss';
import { Onboarded, Onboarding } from './subviews';
import { DynamicCheckInMessageModal, MandatoryLocationModal, ReloadAppModal, TermsAndConditionsModal } from './Modals';

const tcArray = [
  {
    key: 'terms_agreement',
    title: 'mediaReleaseForm'
  },
  {
    key: 'conditions_agreement',
    title: 'legalAgreements'
  },
  {
    key: 'privacy_agreement',
    title: 'privacyStatement'
  }
];

const proceedWithCheckIn = ({ country, campaigns, checkIn, getCampaigns, updateTransientProps }) => {
  if (!COUNTRIES_ADDRESS.AUTOCOMPLETE.includes(country)) {
    checkIn();
    updateTransientProps({ showCheckInDynamicMessageModal: false });
  } else {
    if (campaigns.length === 1) getCampaigns();
    updateTransientProps({ showCheckInDynamicMessageModal: false });
    NavigationService.navigate({
      name: PAGE_NAMES.CHECKIN
    });
  }
};

const navigateCampaign = ({ campaign, getOnboardingProfiles, setUserCampaign, userId }) => {
  setUserCampaign(campaign, false);
  getOnboardingProfiles(userId);
};

const handleNavigate = ({
  callback = false,
  campaign = {},
  selectedCampaign = {},
  updateApplicationProp = mockFunction,
  updateCampaignProps = mockFunction,
  user = {},
  value = ''
}) => {
  if (callback) {
    updateCampaignProps({ [user?.id]: { ...campaign[user.id], selectedCampaignFilterId: selectedCampaign?.id } });
  }
  NavigationService.navigate({
    name: PAGE_NAMES.PROFILE
  });
  updateApplicationProp('currentPage', value);
  updateApplicationProp('selectedAppBottomBarSection', {
    label: 'Profile',
    pageName: PAGE_NAMES.PROFILE
  });
};

const processSectionValue = ({ user, empty_pages }, sectionValue) => {
  const [, { fields }] = sectionValue;
  fields.forEach(field => {
    const page = EMPTY_TABS[field];
    if (!empty_pages[page]) {
      empty_pages[page] = { blank_fields: [], name: page };
    }
    if (!empty_pages[page].blank_fields.includes(field) && !user?.blank_fields?.includes(field)) {
      empty_pages[page].blank_fields.push(field);
    }
  });
};

const renderEmptyPages = (emptyPages, blankField) => {
  const page = EMPTY_TABS[blankField];
  emptyPages[page] = emptyPages[page] || { blank_fields: [], name: page };
  emptyPages[page].blank_fields.push(blankField);
  return emptyPages;
};

const renderCampaign = (
  { campaign = [], getOnboardingProfiles = mockFunction, setUserCampaign = mockFunction, userId },
  item,
  index
) => {
  const isSelectedCampaign = item.id === campaign?.[userId]?.selectedCampaign?.id;

  return (
    <div
      key={index}
      className={'campaign-tab'}
      onClick={
        campaign?.[userId]?.selectedCampaign?.id !== item.id
          ? navigateCampaign.bind(null, {
              campaign: item,
              getOnboardingProfiles,
              setUserCampaign,
              userId
            })
          : mockFunction
      }>
      <ColumnView>
        {item?.icon_logo_url !== null ? (
          <img src={item?.icon_logo_url} className="tab-icon-size" />
        ) : (
          <CampaignHand fillColor={isSelectedCampaign ? ICON_COLOR.BLUE : ICON_COLOR.GRAY} />
        )}
        <RowView key={index} marginTop={8}>
          <Link tabLink={campaign[userId]?.selectedCampaign?.id} tabLinkActive={isSelectedCampaign}>
            <span className={`tab-label ${isSelectedCampaign && 'active'}`}>{item.name.toUpperCase()}</span>
          </Link>
        </RowView>
      </ColumnView>
    </div>
  );
};

const renderTab = ({ userId }, campaign, index) => {
  const selectedCampaignId = campaign?.[userId]?.selectedCampaign?.id;
  return <Tab className={'background'} id={selectedCampaignId} key={index} tabActive={selectedCampaignId} />;
};

const handleTermsAndConditionsList = (updateTransientProps, item) => {
  updateTransientProps({ renderKey: item.key });
  NavigationService.navigate({
    name: 'TermsAndConditions'
  });
  updateTransientProps({ tc_modal: false });
};

const termsAndConditionsMessage = updateTransientProps => (
  <div className="text-sm font-light">
    {I18n.t('general:tcAgreement')}
    {tcArray.map((item, index) => (
      <span key={index}>
        <span className="title-links" onClick={handleTermsAndConditionsList.bind(null, updateTransientProps, item)}>
          {I18n.t(`general:${item.title}`)}
        </span>
        {index < tcArray.length - 2 ? ', ' : index === tcArray.length - 2 ? `${' '}${I18n.t('general:and')}${' '}` : ''}
      </span>
    ))}
  </div>
);

const updateUserTC = ({ updateUser, updateTransientProps }) => {
  updateUser({ data: { attributes: { tc_accepted: true } } });
  updateTransientProps({ tc_modal: false, tc_accepted: true });
};

const renderBanner = ({ forceSendToDonor = false, isOffline = false, test_mode }) => {
  let bannerTitle = '';
  let content = '';
  let showBanner = false;

  if (test_mode && !isOffline) {
    bannerTitle = I18n.t('settings:testMode');
    content = I18n.t('settings:testModeContent');
    showBanner = test_mode && !isOffline;
  }

  if (forceSendToDonor && isOffline) {
    bannerTitle = I18n.t('general:forceSendToDonorBanner:title');
    content = I18n.t('general:forceSendToDonorBanner:description');
    showBanner = forceSendToDonor && isOffline;
  }

  if (isOffline) {
    bannerTitle = I18n.t('general:isOfflineBanner:title');
    content = I18n.t('general:isOfflineBanner:description');
    showBanner = isOffline;
  }

  return <Banner show={showBanner} type={ALERT_TYPES.INFO} title={bannerTitle} content={content} />;
};

const handleStartFlow = ({
  checkInObject,
  flowId,
  getPublishedFlows,
  isOffline,
  NavigationService,
  updateTransientProps
}) => {
  if (isOffline) {
    NavigationService.navigate({
      name: 'Flow',
      flowId
    });
  } else {
    updateTransientProps({ loadingFlow: true });
    getPublishedFlows(checkInObject);
  }
};

const Campaigns = ({
  campaign = {},
  campaigns = [],
  checkIn = mockFunction,
  checkInObject = {},
  checkOut = mockFunction,
  currentPage = '',
  flow = {},
  flowId = null,
  flowReady = false,
  getAttachments = mockFunction,
  getCampaigns = mockFunction,
  getFlow = mockFunction,
  getOnboardingProfiles = mockFunction,
  getPublishedFlows = mockFunction,
  getPublishedFlowsOnCheckIn = mockFunction,
  getRoles = mockFunction,
  getUser = mockFunction,
  hasValidBadge = false,
  isCheckedIn = false,
  isOffline = false,
  profilePages = [],
  selectedCampaign = {},
  selectedCampaignPublishedFlowId = '',
  setUserCampaign = mockFunction,
  transient = {},
  updateApplicationProp = mockFunction,
  updateCampaignProps = mockFunction,
  updateProp = mockFunction,
  updateTransientProps = mockFunction,
  updateUser = mockFunction,
  updateUserProps = mockFunction,
  user = {}
}) => {
  const {
    checkOutLoading,
    loadingCampaigns,
    loadingFlow,
    processing,
    showDeniedLocationModal = false,
    showCheckInDynamicMessageModal = false,
    tc_modal = false
  } = transient;

  const { check_in_message = '' } = selectedCampaign;
  const { first_name, id: userId, needed_for_check_in, test_mode } = user;

  const isOnboarded = !!campaign?.[userId]?.campaigns?.length;
  const neededForCheckIn = !needed_for_check_in?.length && isOnboarded;
  const forceSendToDonor = flow?.flows[selectedCampaignPublishedFlowId]?.force_send_to_donor;
  const isProcessing = processing || (isCheckedIn && !flowReady) || loadingFlow || loadingCampaigns;
  const isDisabled =
    !(isCheckedIn && flowReady) ||
    (isOffline && forceSendToDonor) ||
    loadingFlow ||
    checkOutLoading ||
    loadingCampaigns;

  const device = getDevice();

  const [showModal, setShowModal] = useState();

  const determineUserDevice = (userDevice, updateTransientProps) => {
    if (userDevice.android) {
      updateTransientProps({ device: 'android' });
    }
    if (userDevice.ios || userDevice.ipad) {
      updateTransientProps({ device: 'ios' });
    }
  };

  useEffect(() => {
    getOnboardingProfiles(userId);
    getUser(userId);
    getRoles();
  }, []);

  useEffect(() => {
    if (isCheckedIn && !flowReady && selectedCampaignPublishedFlowId) {
      getFlow(selectedCampaignPublishedFlowId);
    }
  }, [flowReady]);

  useEffect(() => {
    if (campaign?.[userId]?.selectedCampaign === undefined && !!campaign?.[userId]?.campaigns?.length) {
      setUserCampaign(campaign?.[userId]?.campaigns?.[0], false);
    }
  }, [campaign]);

  useEffect(() => {
    updateTransientProps({ showOnboardedState: false });
    if (user?.campaign?.id === undefined) {
      getAttachments();
    }
  }, []);

  useEffect(() => {
    if (isCheckedIn && flowReady) {
      updateTransientProps({ processing: false });
    }
  }, [isCheckedIn, flowReady]);

  /*
  small hack by using useState to control f7 popup 
   */
  useEffect(() => {
    setShowModal(true);
  }, []);

  useEffect(() => {
    const empty_pages = (user?.blank_fields || []).reduce(renderEmptyPages, {});
    const rejected_sections = user?.onboarding_profile?.rejected_sections || {};

    Object.entries(rejected_sections).forEach(processSectionValue.bind(null, { user, empty_pages }));

    if (empty_pages && Object.keys(empty_pages).length > 0) {
      updateUserProps({ empty_pages });
    }
  }, [user.blank_fields, user?.onboarding_profile?.rejected_sections]);

  useEffect(() => {
    determineUserDevice(device, updateTransientProps);
  }, []);

  return (
    <Page id={'campaigns'}>
      {isOnboarded ? (
        <>
          <AppHeader
            title={`${first_name} ${I18n.t('general:campaigns')}`}
            banner={renderBanner({ forceSendToDonor, isOffline, test_mode })}>
            <ToolbarF7 className="campaign-navbar" position={'top'} tabbar scrollable noShadow noHairLine>
              {campaigns?.map(
                renderCampaign.bind(null, {
                  campaign,
                  getOnboardingProfiles,
                  setUserCampaign,
                  userId
                })
              )}
            </ToolbarF7>
          </AppHeader>
          <ColumnView>
            <Tabs>{campaigns?.map(renderTab.bind(null, { userId }))}</Tabs>
          </ColumnView>
        </>
      ) : (
        <AppHeader title={I18n.t('general:home')} banner={renderBanner({ isOffline })} />
      )}
      {isOnboarded ? (
        <Onboarded
          campaign={campaign}
          campaigns={campaigns}
          checkIn={checkIn}
          checkInObject={checkInObject}
          checkOut={checkOut}
          currentPage={currentPage}
          flowReady={flowReady}
          getPublishedFlowsOnCheckIn={getPublishedFlowsOnCheckIn}
          handleNavigate={handleNavigate}
          hasValidBadge={hasValidBadge}
          isCheckedIn={isCheckedIn}
          isOffline={isOffline}
          profilePages={profilePages}
          selectedCampaign={selectedCampaign}
          transient={transient}
          updateApplicationProp={updateApplicationProp}
          updateCampaignProps={updateCampaignProps}
          updateProp={updateProp}
          updateTransientProps={updateTransientProps}
          user={user}
        />
      ) : (
        <Onboarding
          campaign={campaign}
          currentPage={currentPage}
          handleNavigate={handleNavigate}
          profilePages={profilePages}
          selectedCampaign={selectedCampaign}
          updateApplicationProp={updateApplicationProp}
          updateCampaignProps={updateCampaignProps}
          updateProp={updateProp}
          user={user}
        />
      )}
      {neededForCheckIn && isOnboarded && (
        <Fab className={'w-full px-20'} position={'center-bottom'} slot={'fixed'}>
          <Button.Primary
            processing={isProcessing}
            iconLeft={!processing && !loadingFlow && <PlayArrow />}
            disabled={isDisabled}
            width={'100%'}
            onClick={handleStartFlow.bind(null, {
              checkInObject,
              flowId,
              getPublishedFlows,
              isOffline,
              updateTransientProps,
              NavigationService
            })}>
            {I18n.t('campaign:startFlow')}
          </Button.Primary>
        </Fab>
      )}

      {showModal && (
        <TermsAndConditionsModal
          tc_modal={tc_modal}
          termsAndConditionsMessage={termsAndConditionsMessage}
          updateTransientProps={updateTransientProps}
          updateUser={updateUser}
          updateUserTC={updateUserTC}
        />
      )}

      <MandatoryLocationModal
        showDeniedLocationModal={showDeniedLocationModal}
        device={transient.device}
        updateTransientProps={updateTransientProps}
      />

      <ReloadAppModal isOpen={transient?.showReloadAppModal} />

      <DynamicCheckInMessageModal
        onCloseModal={proceedWithCheckIn.bind(null, {
          country: user.country,
          campaigns,
          checkIn,
          getCampaigns,
          updateTransientProps
        })}
        message={check_in_message}
        showCheckInDynamicMessageModal={showCheckInDynamicMessageModal}
      />
    </Page>
  );
};

Campaigns.propTypes = {
  campaign: PropTypes.object,
  campaigns: PropTypes.array,
  checkIn: PropTypes.func,
  checkInObject: PropTypes.object,
  checkOut: PropTypes.func,
  currentPage: PropTypes.string,
  flow: PropTypes.object,
  flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowReady: PropTypes.bool,
  getAttachments: PropTypes.func,
  getCampaigns: PropTypes.func,
  getFlow: PropTypes.func,
  getOnboardingProfiles: PropTypes.func,
  getPublishedFlows: PropTypes.func,
  getPublishedFlowsOnCheckIn: PropTypes.func,
  getRoles: PropTypes.func,
  getUser: PropTypes.func,
  hasValidBadge: PropTypes.bool,
  isCheckedIn: PropTypes.bool,
  isOffline: PropTypes.bool,
  profilePages: PropTypes.array,
  selectedCampaign: PropTypes.object,
  selectedCampaignPublishedFlowId: PropTypes.string,
  setUserCampaign: PropTypes.func,
  transient: PropTypes.object,
  updateApplicationProp: PropTypes.func,
  updateCampaignProps: PropTypes.func,
  updateProp: PropTypes.func,
  updateTransientProps: PropTypes.func,
  updateUser: PropTypes.func,
  updateUserProps: PropTypes.func,
  user: PropTypes.object
};

export default Campaigns;
